import { useCallback, useMemo, useState } from 'react';
import {
  useGenerateCardCreation,
  useValidateCardCreation,
} from '@xhype/card-app-sdk';
import { useExecuteTransactions } from '@package/smart-wallet/hooks';
import { useSmartWallet } from '@package/smart-wallet/providers';
import { useAuthStore } from '@package/smart-wallet/store';
import {
  isSignerInitialized,
  parseTransaction,
} from '@package/smart-wallet/utils';
import * as Sentry from '@sentry/react';

export const useHandleCardCreation = () => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [isCardCreationLoading, setIsCardCreationLoading] = useState(false);
  const id = useAuthStore((store) => store.id);
  const { buildTransactions, executeTransactions } = useExecuteTransactions();
  const { smartAccountAddress, isLoading: isLoadingSmartWallet } =
    useSmartWallet();

  /* Mutations */
  const { mutateAsync: generateCardCreation } = useGenerateCardCreation({
    onError: (error) => {
      console.error('=== Card creation generation error: ', error);
      Sentry.captureException({
        message: 'Card creation generation error',
        extra: {
          error,
        },
      });
    },
  });

  const { mutateAsync: validateCardCreation } = useValidateCardCreation({
    onError: (error) => {
      console.error('=== Card creation validation error: ', error);
      Sentry.captureException({
        message: 'Card creation validation error',
        extra: {
          error,
        },
      });
    },
  });

  /* Memo */
  const isLoading = useMemo(() => {
    return isLoadingSmartWallet || isCardCreationLoading;
  }, [isLoadingSmartWallet, isCardCreationLoading]);

  /* Callbacks */
  const handleCardCreation = useCallback(
    async (holderId: number, tierId: number, couponCode?: string) => {
      try {
        setError(undefined);
        setIsCardCreationLoading(true);
        isSignerInitialized(smartAccountAddress ?? '');

        if (id === undefined) {
          throw new Error('User not initialized');
        }

        const { data: transactionData } = await generateCardCreation({
          userId: id,
          holderId,
          tierId,
          couponCode,
        });

        const quotes = transactionData.quotes;
        const transactionId = transactionData.txId.toString();

        if (quotes.length === 0) {
          throw new Error('No quotes found');
        }

        const parsedQuotes = parseTransaction(quotes);
        const { transactions } = buildTransactions({
          transactions: parsedQuotes,
        });

        const txHash = await executeTransactions({
          transactions,
        });

        if (!txHash) {
          throw new Error('Transaction hash is undefined');
        }

        const { success } = await validateCardCreation({
          transactionId,
          txHash,
        });

        if (!success) {
          throw new Error('Card creation validation failed');
        }

        return txHash;
      } catch (error) {
        console.error('Error sending card creation transaction: ', error);
        setError(`Error sending card creation transaction: ${error}`);
        Sentry.captureException({
          message: 'Error sending card creation transaction',
          extra: {
            error,
          },
        });
        throw new Error(`Error sending card creation transaction: ${error}`);
      } finally {
        setIsCardCreationLoading(false);
      }
    },
    [
      smartAccountAddress,
      buildTransactions,
      executeTransactions,
      validateCardCreation,
      generateCardCreation,
      id,
    ],
  );

  /* Return */
  return {
    error,
    isLoading,
    handleCardCreation,
  };
};
